/**
 * Created by neo on 10.01.2026
 * My Impact - User's personal impact tracking and statistics
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { LocalizedValue, createLocalizedValue } from '../../LocalizedValue';

export type ImpactType = 'TREES' | 'WATER' | 'CUSTOM';

/**
 * Summary of athlete's impact grouped by template (e.g., all "Tree Planting" campaigns)
 */
export type AthleteTemplateImpactSummaryJson = {
  templateId: string;
  templateName: Partial<LocalizedValue>[];
  impactType: ImpactType;
  iconEmoji: string;
  unitNameSingular: Record<string, string>;
  unitNamePlural: Record<string, string>;
  totalUnits: number;
  totalCo2: number;
  totalAwards: number;
  campaignCount: number;
  partnerName?: string;
  partnerLogoUrl?: string;
};

export type AthleteTemplateImpactSummary = Omit<AthleteTemplateImpactSummaryJson, 'templateName'> & {
  templateName: LocalizedValue[];
  getName(language: string): string;
  getUnitName(language: string, amount: number): string;
};

export function createAthleteTemplateImpactSummary(
  json?: Partial<AthleteTemplateImpactSummaryJson>,
): AthleteTemplateImpactSummary {
  return makeAutoObservable({
    templateId: json?.templateId ?? '',
    templateName: (json?.templateName ?? []).map((v) => createLocalizedValue(v)),
    impactType: json?.impactType ?? 'TREES',
    iconEmoji: json?.iconEmoji ?? '🌳',
    unitNameSingular: json?.unitNameSingular ?? { en: 'unit', de: 'Einheit' },
    unitNamePlural: json?.unitNamePlural ?? { en: 'units', de: 'Einheiten' },
    totalUnits: json?.totalUnits ?? 0,
    totalCo2: json?.totalCo2 ?? 0,
    totalAwards: json?.totalAwards ?? 0,
    campaignCount: json?.campaignCount ?? 0,
    partnerName: json?.partnerName,
    partnerLogoUrl: json?.partnerLogoUrl,

    getName(language: string): string {
      const localized = this.templateName.find((v) => v.lang === language);
      if (localized) return localized.value;
      const fallback = this.templateName.find((v) => v.lang === 'en') ?? this.templateName[0];
      return fallback?.value ?? '';
    },

    getUnitName(language: string, amount: number): string {
      const units = amount === 1 ? this.unitNameSingular : this.unitNamePlural;
      return units[language] ?? units['en'] ?? Object.values(units)[0] ?? 'unit';
    },
  });
}

/**
 * API methods for My Impact endpoints
 */
export const MyImpactApi = {
  getTemplateSummaries(language: string = 'en'): Promise<AthleteTemplateImpactSummary[]> {
    return HttpBackend.get('/coach-program/engagement/impact/my/templates', { language })
      .then((response) =>
        (response ?? []).map((r: AthleteTemplateImpactSummaryJson) => createAthleteTemplateImpactSummary(r)),
      )
      .catch(() => []);
  },
};
